import { CommonModule } from '@angular/common';
import { Component, computed, input, ViewChild } from '@angular/core';
import { DwGridDataSourceService, FieldTypes } from '@digiwall/ag-grid';
import {
  DwGridRequestDto,
  FilterModel,
  SupplierOpenOrderLinesService,
  SupplierOpenOrderLinesViewDto,
} from '@digiwall/goliath-documents/proxy';
import { LocalizationService, SubscriptionService, LocalizationModule } from '@abp/ng.core';
import { ColDef } from '@ag-grid-community/core';
import { ListPageComponent } from '@digiwall/ag-grid';
import { AgGridComponent } from '@digiwall/ag-grid';
import { GoliathDocumentEntityNames } from '../../constants/entity-names.constants';
import { OpenOrderLinesListComponent } from '../open-order-lines/open-order-lines-list.component';

@Component({
  selector: 'document-supplier-open-order-lines-list',
  templateUrl: './supplier-open-order-lines-list.component.html',
  styles: `
    :host {
      flex-grow: 1;
    }
  `,
  providers: [DwGridDataSourceService, SubscriptionService],
  imports: [CommonModule, ListPageComponent, AgGridComponent, LocalizationModule],
})
export class SupplierOpenOrderLinesListComponent extends OpenOrderLinesListComponent<
  SupplierOpenOrderLinesViewDto,
  SupplierOpenOrderLinesService
> {
  @ViewChild('agGrid') grid!: AgGridComponent;

  protected thirdPartyCode = input<string>();
  protected articleCode = input<string>();

  protected GoliathDocumentEntityNames: any = GoliathDocumentEntityNames;

  public columns = computed(() => {
    this.grid?.refreshGrid();
    if (this.thirdPartyCode()?.length > 0) {
      // Remove third party columns
      const toRemove = new Set<string>(['supplierCode', 'supplierName']);
      return this._columns.filter(x => !toRemove.has(x.field));
    }
    if (this.articleCode()?.length > 0) {
      // Remove article column
      const toRemove = new Set<string>(['articleCode']);
      return this._columns.filter(x => !toRemove.has(x.field));
    } else {
      return this._columns;
    }
  });

  private _columns: ColDef[] = [
    ...this.colDefs,
    {
      field: 'estimatedDate',
      headerName: this.localizationService.instant('Doc::OpenOrderLines:EstimatedDate'),
      type: FieldTypes.Date,
    },
    {
      field: 'supplierCode',
      headerName: this.localizationService.instant('Doc::OpenOrderLines:SupplierCode'),
      type: FieldTypes.String,
    },
    {
      field: 'supplierName',
      headerName: this.localizationService.instant('Doc::OpenOrderLines:SupplierName'),
      type: FieldTypes.String,
    },
    {
      field: 'receivedQuantity',
      headerName: this.localizationService.instant('Doc::OpenOrderLines:ReceivedQuantity'),
      type: FieldTypes.Number,
      cellRendererParams: {
        decimals: 2,
      },
    },
    {
      field: 'articleRange1',
      headerName: this.localizationService.instant('Doc::StockDoc:ArticleRange1'),
      type: FieldTypes.String,
    },
    {
      field: 'articleRangeValue1',
      headerName: this.localizationService.instant('Doc::StockDoc:ArticleRangeValue1'),
      type: FieldTypes.String,
    },
    {
      field: 'articleRange2',
      headerName: this.localizationService.instant('Doc::StockDoc:ArticleRange2'),
      type: FieldTypes.String,
    },
    {
      field: 'articleRangeValue2',
      headerName: this.localizationService.instant('Doc::StockDoc:ArticleRangeValue2'),
      type: FieldTypes.String,
    },
    {
      field: 'articleLot',
      headerName: this.localizationService.instant('Doc::StockDoc:ArticleLot'),
      type: FieldTypes.String,
    },
    {
      field: 'articleSerial',
      headerName: this.localizationService.instant('Doc::StockDoc:ArticleSerial'),
      type: FieldTypes.String,
    },
  ];

  constructor(
    service: SupplierOpenOrderLinesService,
    datasource: DwGridDataSourceService,
    localizationService: LocalizationService,
  ) {
    super(service, datasource, localizationService);
  }

  protected override getQueryObservable() {
    const baseObservable = super.getQueryObservable();
    return (query: DwGridRequestDto) => {
      if (query?.request != null && this.thirdPartyCode()?.length > 0) {
        query.request.filterModel ??= {};
        query.request.filterModel.supplierCode = {
          filter: this.thirdPartyCode() as unknown as object,
          filterType: 'text',
          type: 'equals',
        } as FilterModel;
      }
      if (query?.request != null && this.articleCode()?.length > 0) {
        query.request.filterModel ??= {};
        query.request.filterModel.articleCode = {
          filter: this.articleCode() as unknown as object,
          filterType: 'text',
          type: 'equals',
        } as FilterModel;
      }
      return baseObservable(query);
    };
  }

  public override selectedRowsClicked($event: Array<SupplierOpenOrderLinesViewDto>) {
    $event.forEach(entity => {
      const url = '/documents/supplier-purchase-orders/' + entity.docId;
      window.open(url, '_blank');
    });
  }
}
